/**
 * ROUTE: src/components/Pruebas/FaseResultadosNatacion.jsx
 * Renderiza los resultados de una fase de natación.
 * Estructura esperada: phase.athletes[] con { pos, time, rankPosition, isDQ, isDNS, notes, athlete }
 */

import React, { useEffect, useState } from "react";
import API_BASE_FORMATOS_URL from "../../config/config_formatos";
import { ATLETISMO_EVENT_ID, capitalize, getMedalColor } from "./pruebas.utils";

// ── SwimmingStatusBadge ───────────────────────────────────────────────────────
const SwimmingStatusBadge = ({ isDQ, isDNS, notes }) => {
  if (isDQ)
    return (
      <span className="text-xs font-bold px-2 py-0.5 rounded bg-red-50 text-red-500 border border-red-200">
        DQ
      </span>
    );
  if (isDNS)
    return (
      <span className="text-xs font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200">
        DNS
      </span>
    );
  if (notes)
    return (
      <span className="text-xs font-bold px-2 py-0.5 rounded bg-yellow-50 text-yellow-600 border border-yellow-200">
        {notes}
      </span>
    );
  return null;
};

// ── AthleteRow ────────────────────────────────────────────────────────────────
const AthleteRow = ({ entry, dimmed = false }) => {
  const athleteInfo = entry.athlete?.athlete;
  const fullName    = athleteInfo?.fullName ?? "—";
  const institution = athleteInfo?.institution?.name ?? "—";
  const logoUrl     = athleteInfo?.institution?.logoUrl ?? null;
  const pos         = entry.rankPosition ?? entry.pos;
  const hasPos      = pos !== null && pos !== undefined;
  const isDisqualified = entry.isDQ || entry.isDNS;

  return (
    <div
      className={`flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-gray-100 shadow-sm transition-opacity ${
        dimmed ? "opacity-50" : ""
      }`}
    >
      {/* Izquierda: pos + logo + nombre */}
      <div className="flex items-center gap-2 min-w-0">
        <span
          className={`text-sm font-bold w-6 text-center flex-shrink-0 ${
            hasPos && !isDisqualified ? getMedalColor(pos) : "text-gray-300"
          }`}
        >
          {hasPos && !isDisqualified ? pos : "—"}
        </span>

        {logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            className="w-6 h-6 rounded-full object-contain flex-shrink-0 bg-gray-50 border border-gray-100"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <span className="w-6 h-6 rounded-full bg-gray-100 flex-shrink-0" />
        )}

        <div className="min-w-0">
          <p className={`font-medium text-sm capitalize truncate ${dimmed ? "text-gray-400" : "text-gray-800"}`}>
            {fullName.toLowerCase()}
          </p>
          <p className="text-xs text-gray-400 capitalize hidden sm:block truncate">
            {capitalize(institution)}
          </p>
        </div>
      </div>

      {/* Derecha: tiempo o badge DNS/DQ */}
      <div className="flex-shrink-0 ml-3">
        {isDisqualified ? (
          <SwimmingStatusBadge isDQ={entry.isDQ} isDNS={entry.isDNS} notes={entry.notes} />
        ) : (
          <span className="font-mono font-bold text-cyan-700 text-sm">
            {entry.time ?? "—"}
          </span>
        )}
      </div>
    </div>
  );
};

// ── FaseResultadosNatacion ────────────────────────────────────────────────────
const FaseResultadosNatacion = ({ eventCategoryId, phaseId }) => {
  const [athletes, setAthletes] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(
      `${API_BASE_FORMATOS_URL}/api/sismaster/competition-report/${ATLETISMO_EVENT_ID}?eventCategoryId=${eventCategoryId}&phaseId=${phaseId}`
    )
      .then((res) => {
        if (!res.ok) throw new Error(`Error ${res.status}`);
        return res.json();
      })
      .then((res) => {
        const phase = res.sports?.[0]?.categories?.[0]?.phases?.[0];
       
        setAthletes(phase?.athletes ?? []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [eventCategoryId, phaseId]);

  if (loading)
    return <p className="text-gray-400 text-sm py-2">Cargando resultados...</p>;
  if (error)
    return <p className="text-red-400 text-sm py-2">Error: {error}</p>;
  if (!athletes || athletes.length === 0)
    return <p className="text-gray-400 text-sm py-2">Sin resultados registrados aún.</p>;

  const clasificados    = athletes.filter((a) => !a.isDQ && !a.isDNS);
  const noClasificados  = athletes.filter((a) =>  a.isDQ || a.isDNS);

  return (
    <div className="flex flex-col gap-1.5">
      {/* Clasificados ordenados por rankPosition */}
      {clasificados
        .slice()
        .sort((a, b) => (a.rankPosition ?? 999) - (b.rankPosition ?? 999))
        .map((entry) => (
          <AthleteRow key={entry.registrationId} entry={entry} />
        ))}

      {/* Separador + DNS/DQ al final */}
      {noClasificados.length > 0 && (
        <>
          <div className="flex items-center gap-2 py-1 px-1">
            <div className="flex-1 h-px bg-gray-200" />
            
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          {noClasificados.map((entry) => (
            <AthleteRow key={entry.registrationId} entry={entry} dimmed />
          ))}
        </>
      )}
    </div>
  );
};

export default FaseResultadosNatacion;